import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  LineChart, Line, BarChart, Bar, ResponsiveContainer,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { ArrowLeft, ShieldCheck, NotePencil, Trash } from "@phosphor-icons/react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import FareCell from "../components/FareCell";
import UpdatedAgo from "../components/UpdatedAgo";
import { fmtAUD, MONTH_NAMES, flagFor } from "../lib/format";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const AIRLINE_COLOR = {
  SA: "#FFB81C", QF: "#E10A0A", EK: "#D71920", SQ: "#1A4D8C",
  QR: "#5C0631", CX: "#006564", VA: "#DF1A2C", ET: "#1B7A3B",
};

export default function RouteDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [matrix, setMatrix] = useState(null);
  const [months, setMonths] = useState(6);
  const [cabin, setCabin] = useState("Y");
  const [trends, setTrends] = useState(null);
  const [gap, setGap] = useState(null);
  const [capacity, setCapacity] = useState([]);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");

  const loadMatrix = useCallback(async () => {
    const r = await api.get(`/routes/${id}/matrix?months=${months}`);
    setMatrix(r.data);
  }, [id, months]);

  const loadTrends = useCallback(async () => {
    const r = await api.get(`/routes/${id}/trends?cabin=${cabin}&months=12`);
    setTrends(r.data);
  }, [id, cabin]);

  const loadGap = useCallback(async () => {
    const r = await api.get(`/routes/${id}/gap?cabin=${cabin}&months=${months}`);
    setGap(r.data);
  }, [id, cabin, months]);

  const loadCapacity = useCallback(async () => {
    const r = await api.get(`/routes/${id}/capacity`);
    setCapacity(r.data);
  }, [id]);

  const loadNotes = useCallback(async () => {
    const r = await api.get(`/routes/${id}/notes`);
    setNotes(r.data);
  }, [id]);

  useEffect(() => { loadMatrix(); loadCapacity(); loadNotes(); }, [loadMatrix, loadCapacity, loadNotes]);
  useEffect(() => { loadTrends(); loadGap(); }, [loadTrends, loadGap]);

  const verify = async () => {
    await api.post(`/routes/${id}/verify`);
    toast.success("Route verified");
    loadMatrix();
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    await api.post(`/routes/${id}/notes`, { text: noteText });
    setNoteText("");
    loadNotes();
    toast.success("Note added");
  };

  const deleteNote = async (nid) => {
    await api.delete(`/routes/${id}/notes/${nid}`);
    loadNotes();
  };

  if (!matrix) {
    return <div className="p-10 text-sm" style={{ color: "var(--text-secondary)" }}>Loading...</div>;
  }

  const route = matrix.route;
  const cabins = ["C", "PY", "Y"];

  // Build chart data for trends
  const trendChart = (() => {
    if (!trends) return [];
    return trends.window.map((w, i) => {
      const row = { label: `${MONTH_NAMES[w.month - 1]} ${String(w.year).slice(-2)}` };
      trends.series.forEach((s) => {
        const pt = s.points[i];
        row[s.airline_iata] = pt?.fare ?? null;
      });
      return row;
    });
  })();

  // Build gap chart
  const gapChart = (() => {
    if (!gap) return [];
    return gap.data.map((d) => {
      const row = { label: `${MONTH_NAMES[d.month - 1]} ${String(d.year).slice(-2)}` };
      d.competitors.forEach((c) => {
        row[c.iata] = c.gap_pct ?? 0;
      });
      return row;
    });
  })();

  const canEdit = user?.role === "admin" || user?.role === "analyst";

  const verifyCell = async (cell) => {
    const body = {
      route_id: route.id,
      airline_iata: cell.airline_iata,
      travel_year: cell.year,
      travel_month: cell.month,
      cabin_class: cell.cabin,
    };
    toast.loading("Verifying with airline-direct + SerpAPI…", { id: "verify" });
    try {
      await api.post("/scrapers/verify-cell", body);
      toast.success("Cell verified", { id: "verify" });
      loadMatrix();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verify failed", { id: "verify" });
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1600px]">
      {/* Header */}
      <div className="mb-6">
        <Link to="/" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.16em] mb-3 hover:text-white transition-colors"
              style={{ color: "var(--text-secondary)" }} data-testid="back-to-dashboard">
          <ArrowLeft size={12} weight="bold" /> Dashboard
        </Link>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-3xl">{flagFor(route.origin_cc)}</span>
              <span className="font-display text-3xl lg:text-4xl font-bold tracking-tight">
                {route.origin} → {route.destination}
              </span>
              <span className="text-3xl">{flagFor(route.destination_cc)}</span>
            </div>
            <div className="text-sm" style={{ color: "var(--text-secondary)" }}>
              {route.origin_city}, {route.origin_country} → {route.destination_city}, {route.destination_country}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <UpdatedAgo iso={matrix.fetched_at} onClick={loadMatrix} />
            <Select value={`${months}`} onValueChange={(v) => setMonths(parseInt(v))}>
              <SelectTrigger className="w-[120px] h-9" data-testid="rd-months">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3">3 months</SelectItem>
                <SelectItem value="6">6 months</SelectItem>
                <SelectItem value="9">9 months</SelectItem>
                <SelectItem value="12">12 months</SelectItem>
              </SelectContent>
            </Select>
            {canEdit && !route.is_verified && (
              <Button onClick={verify} variant="outline" className="h-9" data-testid="verify-btn">
                <ShieldCheck size={14} weight="duotone" className="mr-1" />
                Mark verified
              </Button>
            )}
            {route.is_verified && (
              <div className="flex items-center gap-1 text-xs" style={{ color: "var(--success)" }}>
                <ShieldCheck size={14} weight="fill" /> Verified
              </div>
            )}
          </div>
        </div>
      </div>

      <Tabs defaultValue="matrix" className="w-full">
        <TabsList data-testid="rd-tabs">
          <TabsTrigger value="matrix" data-testid="tab-matrix">Fare Matrix</TabsTrigger>
          <TabsTrigger value="trends" data-testid="tab-trends">Trends</TabsTrigger>
          <TabsTrigger value="gap" data-testid="tab-gap">Gap Analysis</TabsTrigger>
          <TabsTrigger value="capacity" data-testid="tab-capacity">Capacity</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
        </TabsList>

        {/* Matrix tab */}
        <TabsContent value="matrix" className="mt-5">
          <div className="saa-card p-4 overflow-x-auto" data-testid="fare-matrix">
            <table className="border-separate border-spacing-1 min-w-full">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 px-2 py-2 text-left" style={{ background: "var(--card-bg)" }}>
                    <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--text-muted)" }}>
                      Airline
                    </div>
                  </th>
                  {matrix.window.map((w) => (
                    <th
                      key={`${w.year}-${w.month}`}
                      colSpan={3}
                      className="text-center px-2 py-2 font-display text-xs font-semibold"
                    >
                      {MONTH_NAMES[w.month - 1]} {String(w.year).slice(-2)}
                    </th>
                  ))}
                </tr>
                <tr>
                  <th className="sticky left-0 z-10" style={{ background: "var(--card-bg)" }}></th>
                  {matrix.window.map((w) => (
                    cabins.map((cb) => (
                      <th key={`${w.year}-${w.month}-${cb}`}
                          className="text-center text-[10px] uppercase tracking-widest pb-2"
                          style={{ color: "var(--text-muted)" }}>
                        {cb}
                      </th>
                    ))
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix.rows.map((row) => (
                  <tr key={row.airline_iata}>
                    <td
                      className="sticky left-0 z-10 px-3 py-1.5"
                      style={{ background: row.is_saa ? "rgba(255,184,28,0.10)" : "var(--card-bg)" }}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="font-mono font-bold text-sm"
                          style={{ color: row.is_saa ? "var(--saa-gold)" : "var(--text-primary)" }}
                        >
                          {row.airline_iata}
                        </span>
                        <span className="text-[10px] truncate" style={{ color: "var(--text-secondary)" }}>
                          {row.airline_name}
                        </span>
                      </div>
                    </td>
                    {/* For matrix we render Y cabin row first, etc — but data is structured by cabin within airline */}
                    {matrix.window.map((w, wIdx) => (
                      cabins.map((cb) => {
                        const cabinRow = row.cabins.find((c) => c.cabin === cb);
                        const cell = cabinRow?.cells[wIdx];
                        cell.year = w.year; cell.month = w.month;
                        cell.cabin = cb; cell.airline_iata = row.airline_iata;
                        return (
                          <td key={`${row.airline_iata}-${w.year}-${w.month}-${cb}`} className="p-0">
                            <FareCell
                              cell={cell}
                              isSaa={row.is_saa}
                              testid={`cell-${row.airline_iata}-${w.year}-${w.month}-${cb}`}
                              onVerify={verifyCell}
                              canVerify={canEdit && cell.avg_fare != null && !row.is_saa}
                            />
                          </td>
                        );
                      })
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Legend */}
          <div className="mt-4 flex flex-wrap gap-3 text-[11px]" style={{ color: "var(--text-secondary)" }}>
            <LegendDot color="var(--src-duffel)" label="Duffel NDC" />
            <LegendDot color="var(--src-serpapi)" label="Google Flights" />
            <LegendDot color="var(--src-tripcom)" label="Trip.com" />
            <LegendDot color="var(--src-airline)" label="Airline Direct (legacy)" />
            <LegendDot color="var(--src-mock)" label="Estimated" />
            <LegendDot color="var(--src-override)" label="Manual override" />
          </div>
        </TabsContent>

        {/* Trends */}
        <TabsContent value="trends" className="mt-5">
          <div className="saa-card p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--text-muted)" }}>
                  Trends · 12 months
                </div>
                <div className="font-display text-lg font-semibold">Fare evolution by airline</div>
              </div>
              <Select value={cabin} onValueChange={setCabin}>
                <SelectTrigger className="w-[120px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Y">Economy</SelectItem>
                  <SelectItem value="PY">Premium Eco</SelectItem>
                  <SelectItem value="C">Business</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div style={{ width: "100%", height: 380 }}>
              <ResponsiveContainer>
                <LineChart data={trendChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="label" stroke="#8DA8D6" fontSize={11} />
                  <YAxis stroke="#8DA8D6" fontSize={11} tickFormatter={(v) => `A$${(v / 1000).toFixed(1)}k`} />
                  <Tooltip
                    contentStyle={{ background: "#0A1428", border: "1px solid rgba(255,255,255,0.14)" }}
                    formatter={(v) => fmtAUD(v)}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {trends?.series.map((s) => (
                    <Line
                      key={s.airline_iata}
                      type="monotone"
                      dataKey={s.airline_iata}
                      stroke={AIRLINE_COLOR[s.airline_iata] || "#8DA8D6"}
                      strokeWidth={s.airline_iata === "SA" ? 3 : 1.5}
                      dot={false}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </TabsContent>

        {/* Gap */}
        <TabsContent value="gap" className="mt-5">
          <div className="saa-card p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--text-muted)" }}>
                  Gap analysis vs SAA · {months} months
                </div>
                <div className="font-display text-lg font-semibold">
                  Competitor undercut (% cheaper than SAA)
                </div>
              </div>
              <Select value={cabin} onValueChange={setCabin}>
                <SelectTrigger className="w-[120px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Y">Economy</SelectItem>
                  <SelectItem value="PY">Premium Eco</SelectItem>
                  <SelectItem value="C">Business</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div style={{ width: "100%", height: 380 }}>
              <ResponsiveContainer>
                <BarChart data={gapChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="label" stroke="#8DA8D6" fontSize={11} />
                  <YAxis stroke="#8DA8D6" fontSize={11} tickFormatter={(v) => `${v}%`} />
                  <Tooltip
                    contentStyle={{ background: "#0A1428", border: "1px solid rgba(255,255,255,0.14)" }}
                    formatter={(v) => `${v.toFixed(1)}%`}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {route.competitors.map((ai) => (
                    <Bar key={ai} dataKey={ai} fill={AIRLINE_COLOR[ai] || "#8DA8D6"} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </TabsContent>

        {/* Capacity */}
        <TabsContent value="capacity" className="mt-5">
          <div className="saa-card p-5 overflow-x-auto" data-testid="capacity-tab">
            <div className="text-[10px] uppercase tracking-[0.18em] mb-3" style={{ color: "var(--text-muted)" }}>
              Weekly capacity by carrier
              <span className="ml-2 normal-case tracking-normal" style={{ color: "var(--warning)" }}>
                — Unverified scaffolding; admin to confirm against airline schedules
              </span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ color: "var(--text-secondary)" }} className="text-[11px] uppercase tracking-[0.12em]">
                  <th className="text-left pb-2">Airline</th>
                  <th className="text-left pb-2">Weekly flights</th>
                  <th className="text-left pb-2">Aircraft</th>
                  <th className="text-left pb-2">Routing</th>
                  <th className="text-left pb-2">6th freedom</th>
                </tr>
              </thead>
              <tbody>
                {capacity.map((c) => (
                  <tr key={c.id} style={{ borderTop: "1px solid var(--border-subtle)" }}>
                    <td className="py-2.5 font-mono font-semibold" style={{
                      color: c.airline_iata === "SA" ? "var(--saa-gold)" : "var(--text-primary)"
                    }}>{c.airline_iata}</td>
                    <td className="py-2.5">{c.weekly_flights}</td>
                    <td className="py-2.5">{c.aircraft}</td>
                    <td className="py-2.5">{c.routing}</td>
                    <td className="py-2.5">{c.sixth_freedom ? "Yes" : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Notes */}
        <TabsContent value="notes" className="mt-5">
          <div className="saa-card p-5">
            {canEdit && (
              <div className="mb-4">
                <Textarea
                  data-testid="note-textarea"
                  rows={3}
                  placeholder="Add a route note..."
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                />
                <div className="flex justify-end mt-2">
                  <Button onClick={addNote} data-testid="note-add-btn"
                          style={{ background: "var(--saa-gold)", color: "#0A1428" }}>
                    <NotePencil size={14} weight="duotone" className="mr-1" /> Add note
                  </Button>
                </div>
              </div>
            )}
            <div className="space-y-3">
              {notes.length === 0 && (
                <div className="text-sm" style={{ color: "var(--text-secondary)" }}>
                  No notes yet.
                </div>
              )}
              {notes.map((n) => (
                <div key={n.id} className="p-3 rounded-lg" style={{ background: "var(--card-bg-2)" }}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm whitespace-pre-wrap">{n.text}</div>
                    {user?.role === "admin" && (
                      <button onClick={() => deleteNote(n.id)} className="hover:text-red-500"
                              style={{ color: "var(--text-muted)" }}>
                        <Trash size={14} />
                      </button>
                    )}
                  </div>
                  <div className="text-[10px] mt-2 uppercase tracking-[0.16em]" style={{ color: "var(--text-muted)" }}>
                    {n.author_email} · {new Date(n.created_at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="w-3 h-3 rounded-sm" style={{ background: color, border: "1px solid var(--border-subtle)" }} />
      {label}
    </span>
  );
}
